import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from 'store/hooks';
import logoLetterSvg from 'assets/logo-letter.svg';
import SharkIcon from 'assets/levels/shark.webp';
import DolphinIcon from 'assets/levels/dolphin.webp';
import OctopusIcon from 'assets/levels/octopus.webp';
import FishIcon from 'assets/levels/fish.webp';
import CrabsIcon from 'assets/levels/crab.webp';
import WhaleIcon from 'assets/levels/whale.webp';
import Skeleton from 'components/skeleton/Skeleton';
import styles from './vipAllLevels.module.scss';

const getLevelIcon = (levelName: string, props: any = {}) => {
  switch (levelName.toLowerCase()) {
    case 'crab':
      return <img src={CrabsIcon} alt="Crab" width={40} className={props.className} />;
    case 'octopus':
      return <img src={OctopusIcon} alt="Octopus" width={39} className={props.className} />;
    case 'fish':
      return <img src={FishIcon} alt="Fish" width={40} className={props.className} />;
    case 'dolphin':
      return <img src={DolphinIcon} alt="Dolphin" width={40} className={props.className} />;
    case 'shark':
      return <img src={SharkIcon} alt="Shark" width={40} className={props.className} />;
    case 'whale':
      return <img src={WhaleIcon} alt="Whale" width={48} className={props.className} />;
    default:
      return <img src={logoLetterSvg} alt={levelName} width={26} className={props.className} />;
  }
};

const formatBonusLabel = (type: string): string => {
  return type
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const formatThreshold = (valStr: string): string => {
  const val = parseInt(valStr, 10);
  if (isNaN(val)) return valStr;
  if (val >= 1000000) {
    const formatted = val / 1000000;
    return `$${Number(formatted.toFixed(formatted % 1 === 0 ? 0 : 1))}M`;
  }
  if (val >= 1000) {
    const formatted = val / 1000;
    return `$${Number(formatted.toFixed(formatted % 1 === 0 ? 0 : 1))}K`;
  }
  return `$${val}`;
};

const VipAllLevels: React.FC = () => {
  const { t } = useTranslation();
  const {
    bonusMatrix: data,
    isBonusMatrixLoading: isLoading,
    bonusMatrixError: error,
  } = useAppSelector((state) => state.level);

  if (isLoading || !data) {
    return (
      <div className={styles.allLevelsContainer} style={{ opacity: 0.7 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', padding: '24px' }}>
          <Skeleton variant="rectangular" height={100} borderRadius={12} animation="shimmer" />
          <Skeleton variant="rectangular" height={50} borderRadius={12} animation="shimmer" />
          <Skeleton variant="rectangular" height={50} borderRadius={12} animation="shimmer" />
          <Skeleton variant="rectangular" height={50} borderRadius={12} animation="shimmer" />
        </div>
      </div>
    );
  }

  const bonusDefinitionsMap = new Map<string, string>();
  if (Array.isArray(data.bonusDefinitions)) {
    data.bonusDefinitions.forEach((def: any) => {
      if (def?.bonusType && def?.title) {
        bonusDefinitionsMap.set(def.bonusType, def.title);
      }
    });
  }

  const getBonusLabel = (type: string): string => {
    return bonusDefinitionsMap.get(type) || formatBonusLabel(type);
  };

  const bonusTypes = data.bonusTypes || [];
  const tiers = data.tiers || [];

  return (
    <div className={styles.allLevelsContainer}>
      <div className={styles.gridWrapper}>
        {/* Desktop Grid Layout */}
        <div className={styles.desktopGrid}>
          <div className={styles.perksColumn}>
            <div className={styles.headerCell}>
              <span className={styles.perksTitle}>{t('rewards.allLevels.perks')}</span>
            </div>
            {bonusTypes.map((bonusType: string, idx: number) => (
              <div key={idx} className={styles.perkNameCell}>
                {getBonusLabel(bonusType)}
              </div>
            ))}
          </div>

          <div className={styles.levelsScrollArea}>
            {tiers.map((tierData: any, lIdx: number) => {
              return (
                <div key={lIdx} className={styles.levelColumn}>
                  <div className={styles.headerCell}>
                    <div className={styles.levelLogo}>
                      {getLevelIcon(tierData.label, { className: styles.logoImage })}
                    </div>
                    <span className={styles.levelName}>{tierData.label}</span>
                    <span className={styles.levelWager}>{formatThreshold(tierData.threshold)}</span>
                  </div>

                  {bonusTypes.map((bonusType: string, pIdx: number) => {
                    const hasPerk = !!tierData.bonuses?.[bonusType];
                    return (
                      <div key={pIdx} className={styles.checkCell}>
                        {hasPerk ? (
                          <svg
                            className={styles.checkIcon}
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <polyline points="20 6 9 17 4 12"></polyline>
                          </svg>
                        ) : (
                          <span className={styles.crossIcon}>×</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>

        {/* Mobile Grid Layout */}
        <div className={styles.mobileGrid}>
          <div className={styles.mobileHeaderRow}>
            {tiers.map((tierData: any, lIdx: number) => {
              return (
                <div key={lIdx} className={styles.mobileLevelHeader}>
                  <div className={styles.levelLogo}>
                    {getLevelIcon(tierData.label, { className: styles.logoImage })}
                  </div>
                  <span className={styles.levelName}>{tierData.label}</span>
                </div>
              );
            })}
          </div>

          <div className={styles.mobilePerksContainer}>
            {bonusTypes.map((bonusType: string, pIdx: number) => (
              <div key={pIdx} className={styles.mobilePerkGroup}>
                <div className={styles.mobilePerkTitle}>{getBonusLabel(bonusType)}</div>
                <div className={styles.mobilePerkValues}>
                  {tiers.map((tierData: any, lIdx: number) => {
                    const hasPerk = !!tierData.bonuses?.[bonusType];
                    return (
                      <div key={lIdx} className={styles.mobileCheckCell}>
                        {hasPerk ? (
                          <svg
                            className={styles.checkIcon}
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <polyline points="20 6 9 17 4 12"></polyline>
                          </svg>
                        ) : (
                          <span className={styles.crossIcon}>×</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VipAllLevels;
